import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import { LocationQuery, useRoute, useRouter } from 'vue-router'

import {
    DEFAULT_CLIDS,
    NATIVE_APP_CLIDS,
    OPPO_WL_CLID,
    PLAYGAMA_CLIDS,
    PWA_APP_CLIDS,
    TEST_GAMES,
    WHITE_LABEL_CLIDS,
} from '@/constants/general'
import { ROUTE_NAMES } from '@/router/constants'
import {
    CLID_PARAM,
    DEBUG_PARAM,
    EXPORT_MODE_PARAM,
    GAME_DISTRIBUTION_PARAM,
    WIDGET_ID_PARAM,
} from '@/router/query-params'
import { isBestWebGamesHost } from '@/router/white-label/utils'
import type { SelectedExperimentsData } from '@/server/experiments/types'
import type { ClientType, EffectiveType, PlatformType, RankingParams } from '@/types'
import {
    getLandingClidFromCookies,
    getSessionCookie,
    setSessionCookie,
    updateLandingClidCookie,
    updateRankingCookies,
} from '@/utils/cookie-parser'
import { compareVersionStrings, isDesktopSafari, isDeviceIOS } from '@/utils/device'
import { getUTCYearWeek, idleCallbackPromise } from '@/utils/helpers'
import { useAPI } from '@/utils/requests/api-plugin'
import { buildSessionCookieValue, getUtmHashFromQuery, parseSessionCookieValue } from '@/utils/session'

export type ErrorStatus = 404 | 410 | 500
export type PageStatus = 200 | 301 | 302 | ErrorStatus

const DEFAULT_PLATFORM = 'desktop'
const IOS_IDENTIFIER = 'PlaygamaIOSApp'
const ANDROID_IDENTIFIER = 'PlaygamaAndroidApp'
const IOS_NATIVE_PAYMENTS_MIN_VERSION = '1.1.0'
const ANDROID_NATIVE_PAYMENTS_MIN_VERSION = '1.0.35'

export const useAppStore = defineStore('app', () => {
    const pageStatus = ref<PageStatus>(200)
    const targetURL = ref<string>('')
    const platform = ref<PlatformType>(DEFAULT_PLATFORM)
    const host = ref<string>()
    const referer = ref<string>()
    const userAgent = ref<string>()
    const auid = ref<string>()
    const countryCode = ref<string | undefined>()
    const effectiveType = ref<EffectiveType | undefined>()
    const rawRankingParams = ref<RankingParams>()
    const userAcquisitionParams = ref<Record<string, string>>({})
    const rawContentScopeCookie = ref<string | undefined>()
    const landingAttributesCookie = ref<string | undefined>()
    const query = ref<LocationQuery>()
    const isGameDistribution = computed(() => !!query.value?.[GAME_DISTRIBUTION_PARAM])
    const isOppoWL = computed(() => isBestWebGamesHost(host.value))
    const isDebugMode = computed(() => !!query.value?.[DEBUG_PARAM])
    const isNativeIosEnv = computed(() => userAgent.value?.includes(IOS_IDENTIFIER) ?? false)

    const isNativeAndroidEnv = computed(() => userAgent.value?.includes(ANDROID_IDENTIFIER) ?? false)

    const isNativeAppEnv = computed(
        () => isNativeIosEnv.value || isNativeAndroidEnv.value || Object.values(NATIVE_APP_CLIDS).includes(clid.value),
    )

    const appVersion = computed(() => {
        if (!userAgent.value) {
            return undefined
        }
        const regex = new RegExp(`(?:${IOS_IDENTIFIER}|${ANDROID_IDENTIFIER})/([\\d.]+)`)
        const match = userAgent.value.match(regex)

        return match ? match[1] : undefined
    })
    const isPWA = computed(() => Object.values(PWA_APP_CLIDS).includes(clid.value))
    const isAppEnv = computed(() => isNativeAppEnv.value || isPWA.value)
    const clientType = computed((): ClientType => {
        if (isNativeAppEnv.value) {
            return 'mobile-native'
        }
        if (platform.value !== 'desktop') {
            return 'mobile-web'
        }
        return 'desktop-web'
    })
    function isVersionAtLeast(minVersions: { ios?: string; android?: string }): boolean {
        if (!appVersion.value) {
            return false
        }
        if (isNativeIosEnv.value && minVersions.ios) {
            return compareVersionStrings(appVersion.value, minVersions.ios)
        }
        if (isNativeAndroidEnv.value && minVersions.android) {
            return compareVersionStrings(appVersion.value, minVersions.android)
        }
        return false
    }

    const isNativePaymentsSupported = computed(() =>
        isVersionAtLeast({ ios: IOS_NATIVE_PAYMENTS_MIN_VERSION, android: ANDROID_NATIVE_PAYMENTS_MIN_VERSION }),
    )
    const isAuthEnabled = computed(() => !isOppoWL.value)
    const isPurchaseEnabled = computed(() => {
        if (!isAuthEnabled.value) {
            return false
        }

        return isNativeAppEnv.value ? isNativePaymentsSupported.value : true
    })

    const headers = ref<Record<string, string>>({})

    const api = useAPI()
    const route = useRoute()

    const isExportOnWidget = computed(() => query.value?.[EXPORT_MODE_PARAM] === 'widget')
    const isExportOnWhitelabel = computed(() => query.value?.[EXPORT_MODE_PARAM] === 'wl')
    const isPartnerEmbed = computed(
        () =>
            (route.name === ROUTE_NAMES.EXPORT_GAME_PAGE && query.value?.[EXPORT_MODE_PARAM] !== 'portal') ||
            route.name === ROUTE_NAMES.WIDGET_GAME_PAGE ||
            isOppoWL.value,
    )

    const isPortalEnv = computed(() => !isPartnerEmbed.value)

    function setLandingAttributesCookie(value: string | undefined) {
        landingAttributesCookie.value = value
    }

    // Here we save the clid and some context to the cookie
    // the cookie format is "<clid>:<game-hru || 'Main_page'>"
    // the whole cookie values is used as pseudo-widgetId for affiliate link attribution
    function updateLandingAttributes() {
        let currentCookieValue = getLandingClidFromCookies(document.cookie) || ''
        const currentCookieClid = currentCookieValue.split(':')[0] || ''

        // save/update cookie only when clid is explicitly in URL
        // and is not same as in store cookie
        // and we are not in partner embed
        if (route.query.clid && currentCookieClid !== route.query.clid && !isPartnerEmbed.value) {
            currentCookieValue = `${route.query.clid}:${route.params.game || 'Main_page'}`
            updateLandingClidCookie(currentCookieValue)
        }
        setLandingAttributesCookie(currentCookieValue)
    }

    const parsedLandingAttributes = computed(() => {
        const [landingClid] = landingAttributesCookie.value?.split(':') || []
        return { landingClid, landingWidgetId: landingAttributesCookie.value }
    })

    const clid = computed(() => {
        if (TEST_GAMES.includes(route.params.game as string)) {
            return DEFAULT_CLIDS.directTest
        }

        let fallbackClid = DEFAULT_CLIDS.unknown

        if (host.value === 'playgama.com') {
            fallbackClid = DEFAULT_CLIDS.direct
        }

        if (host.value?.endsWith('.playgama.com')) {
            fallbackClid = DEFAULT_CLIDS.directTest
        }

        if (isOppoWL.value) {
            fallbackClid = OPPO_WL_CLID
        }

        return (query.value?.[CLID_PARAM] as string) || parsedLandingAttributes.value.landingClid || fallbackClid
    })

    const contentScope = computed(() => {
        // If isAppEnv is true, always return 'curated'
        if (isAppEnv.value) {
            if (isNativeIosEnv.value) {
                // temporary solution for limiting ios app content
                // no in-app, age 16+
                return 'curated-app'
            }
            return 'curated'
        }
        // Otherwise return the cookie value (if any)
        // return rawContentScopeCookie.value

        // decided to disable curated scope from the cookie
        return undefined
    })

    const widgetId = computed(() => {
        // if widgetId is in URL, use it
        // this is case for regular partner widgets. Both widget page and inner export page will get it
        if (query.value?.[WIDGET_ID_PARAM]) {
            return query.value?.[WIDGET_ID_PARAM] as string | undefined
        }
        const { landingClid, landingWidgetId } = parsedLandingAttributes.value
        const isPlaygamaClid = Object.values(PLAYGAMA_CLIDS).includes(clid.value)

        // this is for portal affiliate traffic
        // when someone lands on playgama.com/game/GAME?clid=XXX we want to send all events with pseudo widgetId
        // to track wich GAME was first and show it in partner interface
        if (
            clid.value === landingClid && // until clid is not overriden
            !isPlaygamaClid && // and clid is not our app/testing/etc
            !isPartnerEmbed.value // and not partner embed
        ) {
            return landingWidgetId
        }

        return undefined
    })

    const queryParams = computed(() => Object.entries(query.value || {}))

    const isAuthBannerOpen = ref<boolean>(false)

    function setHeaders(value: Record<string, string>) {
        headers.value = value
    }

    function getWhiteLabelClid(partner: string) {
        return WHITE_LABEL_CLIDS[partner] || DEFAULT_CLIDS.unknown
    }

    function setPageStatus(status: PageStatus) {
        pageStatus.value = status
    }
    function setUserParams(details: { userAcquisitionParams: Record<string, string> }) {
        userAcquisitionParams.value = details.userAcquisitionParams
    }

    function setContentScopeCookie(value: string | undefined) {
        if (value) {
            rawContentScopeCookie.value = value
        } else {
            rawContentScopeCookie.value = undefined
        }
    }

    function setTargetURL(target: string) {
        targetURL.value = target
    }

    function setPlatform(plt: PlatformType) {
        platform.value = plt
    }

    function setUserAgent(ua?: string) {
        userAgent.value = ua
    }

    function setCountryCode(code?: string) {
        countryCode.value = code
    }

    // new auid format is {randomHash}.{auidTimeInSec}
    // see generateAuidToken in src/server/utils.ts
    function setAuidInfo(token?: string) {
        auid.value = token
    }

    const auidTimestamp = computed(() => {
        if (auid.value) {
            const auidTimestampString = auid.value.split('.')[1]
            if (auidTimestampString) {
                return Number(auidTimestampString) * 1000
            }
            // before that date auids were without timestamp
            return Number(new Date('2025-11-13'))
        }
        return Date.now()
    })

    const cohort = computed(() => {
        return getUTCYearWeek(auidTimestamp.value)
    })

    function setRankingParams(params: RankingParams) {
        rawRankingParams.value = params
    }

    /** @deprecated conflicts with audience-detection-store */
    async function fetchRankingParams() {
        if (auid.value) {
            const { cluster_id, audnc } = await api.getRankingClusterId({
                auid: auid.value,
                platform: platform.value,
                audience: rawRankingParams.value?.audience,
                currentClusterId: rawRankingParams.value?.clusterId,
            })
            // don't set in store because it will change queryKey in tanstack query caches
            updateRankingCookies({
                clusterId: cluster_id || undefined,
                audience: audnc || undefined,
            })
        }
    }

    function persistRankingParams(params: RankingParams) {
        updateRankingCookies(params)
    }

    const rankingParams = computed(() => {
        // AUD3 audience is only used for ranking behind the use_aud3 experiment
        if (experiments.value?.use_aud3?.sample !== 'group_1') {
            return { ...rawRankingParams.value, audience: undefined }
        }
        return rawRankingParams.value
    })

    function setUrlDetails(details: { query: LocationQuery; host: string; referer?: string }) {
        host.value = details.host
        query.value = details.query
        referer.value = details.referer
    }

    const isPortalMobileOrganic = computed(() => {
        if (platform.value === 'desktop' || clid.value !== DEFAULT_CLIDS.direct || !isPortalEnv.value) {
            return false
        }
        const { medium, source } = userAcquisitionParams.value
        return (
            (medium === 'none' && source === 'direct') ||
            (medium === 'referral' && (source?.includes('google') || source?.includes('bing')))
        )
    })

    const experimentsRaw = shallowRef<SelectedExperimentsData | undefined>()
    const isAudienceRankedAUD3 = rawRankingParams.value?.audience === 'AUD3'
    const experiments = computed(() => ({
        use_aud3: isAudienceRankedAUD3 ? experimentsRaw.value?.['use_aud3'] : undefined,
        preroll_interstitial_delay:
            !isAudienceRankedAUD3 && clid.value === DEFAULT_CLIDS.direct
                ? experimentsRaw.value?.['preroll_interstitial_delay']
                : undefined,
    }))

    const experimentsString = computed(() => {
        return Object.entries(experiments.value)
            .map(([key, value]) => (value ? `${key}:${value.sample}` : false))
            .filter((x) => !!x)
            .join(', ')
    })

    function setExperiments(samples?: SelectedExperimentsData) {
        experimentsRaw.value = samples
    }

    // TODO remove this, and targetURL, setTargetURL, setPageStatus all together
    // use router methods instead
    const router = useRouter()
    watch(
        () => [pageStatus.value, targetURL.value],
        () => {
            if (targetURL.value !== '' && pageStatus.value > 300 && pageStatus.value < 400) {
                router.replace(targetURL.value)
                pageStatus.value = 200
                targetURL.value = ''
            }
        },
    )

    const pageLoadProgress = ref(false)
    // this is called in InitWrapper after initial navigation is done
    // so here we track all but first navigation, which is exactly what we want
    function initPageLoadProgressTracking() {
        router.beforeEach(async () => {
            pageLoadProgress.value = true
            // unblock thread
            await idleCallbackPromise()
        })
        router.afterEach(() => {
            pageLoadProgress.value = false
        })
    }

    let initialCall = true
    function getSessionId(): string {
        const { timestamp: cookieTimestamp, utmHash: cookieUtmHash } = parseSessionCookieValue(getSessionCookie())
        const currentUtmHash = getUtmHashFromQuery(query.value || {})

        // refresh the cookie on access to prolongate TTL, if the tab is active
        const needExtendCookie = !document.hidden

        // force update the session if a new tab with utm opens (except child tabs and iframes)
        const needResetCookie =
            initialCall &&
            !document.referrer.startsWith(location.origin) &&
            currentUtmHash !== '' &&
            currentUtmHash !== cookieUtmHash

        let sessionTimestamp = cookieTimestamp || Date.now().toString()

        if (needResetCookie) {
            sessionTimestamp = Date.now().toString()
        }

        if (needResetCookie || needExtendCookie) {
            setSessionCookie(buildSessionCookieValue(sessionTimestamp, currentUtmHash))
        }

        initialCall = false

        return `${sessionTimestamp}.${auid.value}`
    }

    function setEffectiveType() {
        effectiveType.value =
            navigator.connection?.effectiveType ||
            `not_applicable${isDeviceIOS() || isDesktopSafari() ? '_ios' : '_other'}`
    }

    return {
        setPageStatus,
        setTargetURL,
        setUrlDetails,
        setHeaders,
        getWhiteLabelClid,
        initPageLoadProgressTracking,
        getSessionId,
        setEffectiveType,
        headers,
        effectiveType,
        pageStatus,
        targetURL,
        cohort,
        userAcquisitionParams,
        contentScope,
        clid,
        host,
        referer,
        queryParams,
        query,
        widgetId,
        platform,
        isAuthBannerOpen,
        isGameDistribution,
        isDebugMode,
        isExportOnWidget,
        isExportOnWhitelabel,
        isPortalEnv,
        isPortalMobileOrganic,
        auid,
        auidTimestamp,
        rawRankingParams,
        rankingParams,
        userAgent,
        countryCode,
        setAuidInfo,
        setRankingParams,
        fetchRankingParams,
        persistRankingParams,
        setPlatform,
        setUserAgent,
        setUserParams,
        setCountryCode,
        setContentScopeCookie,
        rawContentScopeCookie,
        setLandingAttributesCookie,
        updateLandingAttributes,
        landingAttributesCookie,
        setExperiments,
        experimentsRaw,
        experiments,
        experimentsString,
        pageLoadProgress,
        isPWA,
        isNativeAppEnv,
        isNativeAndroidEnv,
        isNativeIosEnv,
        appVersion,
        isAppEnv,
        isAuthEnabled,
        isPurchaseEnabled,
        isVersionAtLeast,
        clientType,
        isOppoWL,
    }
})
